import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Shield,
  Plus,
  Search,
  Trash2,
  X,
  Lock,
  Check,
  CheckSquare,
  Square,
  Edit3,
  UserCheck,
  SlidersHorizontal,
  Info,
  Layers,
  Sparkles,
  KeyRound,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'
import { SYSTEM_PERMISSIONS, useAuthStore } from '../../stores/authStore'

export default function RolesPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null)

  const { activeRoleName, switchActiveRole, user } = useAuthStore()

  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/roles', { search }],
    queryFn: () => api.get('/roles', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (roleData: any) => {
      if (editingRoleId) {
        return api.put(`/roles/${editingRoleId}`, roleData)
      }
      return api.post('/roles', roleData)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/roles'] })
      if (activeRoleName === variables.name) {
        switchActiveRole(variables.name, variables.permissions)
      }
      toast.success(editingRoleId ? 'دستورات و مجوزهای نقش با موفقیت به‌روزرسانی شد' : 'نقش جدید با موفقیت ایجاد شد')
      handleCloseModal()
    },
    onError: () => toast.error('خطا در ثبت نقش')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/roles'] })
      toast.success('نقش حذف شد')
    },
    onError: () => toast.error('خطا در حذف نقش')
  })

  const handleOpenModal = (role?: any) => {
    if (role) {
      setEditingRoleId(role.id)
      setForm({
        name: role.name || '',
        description: role.description || '',
        permissions: Array.isArray(role.permissions)
          ? role.permissions.map((p: any) => typeof p === 'string' ? p : p.key || `${p.resource}.${p.action}`)
          : SYSTEM_PERMISSIONS.flatMap(g => g.permissions.map(p => p.key))
      })
    } else {
      setEditingRoleId(null)
      setForm({
        name: '',
        description: '',
        permissions: ['dashboard.read', 'pos.read', 'pos.sell']
      })
    }
    setIsOpen(true)
  }

  const handleCloseModal = () => {
    setIsOpen(false)
    setEditingRoleId(null)
    setForm({ name: '', description: '', permissions: [] })
  }

  const togglePermission = (key: string) => {
    setForm(prev => {
      const exists = prev.permissions.includes(key)
      if (exists) {
        return { ...prev, permissions: prev.permissions.filter(p => p !== key) }
      } else {
        return { ...prev, permissions: [...prev.permissions, key] }
      }
    })
  }

  const toggleGroupPermissions = (groupId: string) => {
    const group = SYSTEM_PERMISSIONS.find(g => g.id === groupId)
    if (!group) return
    const groupKeys = group.permissions.map(p => p.key)
    const allChecked = groupKeys.every(k => form.permissions.includes(k))

    setForm(prev => {
      if (allChecked) {
        return { ...prev, permissions: prev.permissions.filter(k => !groupKeys.includes(k)) }
      } else {
        const set = new Set([...prev.permissions, ...groupKeys])
        return { ...prev, permissions: Array.from(set) }
      }
    })
  }

  const items = data?.items || []

  // Predefined initial roles for testing if DB items list is empty or minimal
  const defaultRolesList = [
    {
      id: 'role_admin',
      name: 'مدیر سیستم',
      description: 'دسترسی کامل و بدون محدودیت به تمامی بخش‌های نرم‌افزار، تنظیمات مالی و مدیریت نقش‌ها',
      permissions: SYSTEM_PERMISSIONS.flatMap(g => g.permissions.map(p => p.key)),
      permissions_count: 20
    },
    {
      id: 'role_cashier',
      name: 'صندوق‌دار',
      description: 'دسترسی محدود فقط به بخش صندوق، ثبت فاکتور فروش نقدی و مشاهده رسیدها بدون دسترسی به تنظیمات و حسابداری',
      permissions: ['dashboard.read', 'pos.read', 'pos.sell', 'pos.receipts', 'pos.shift', 'pos.payment', 'customer.read'],
      permissions_count: 7
    },
    {
      id: 'role_warehouse',
      name: 'انباردار',
      description: 'دسترسی فقط به بخش مدیریت کالا، ثبت ورود، انبارگردانی و اصلاح موجودی بدون دسترسی به صندوق و صورت‌های مالی',
      permissions: ['dashboard.read', 'inventory.read', 'product.read', 'product.write', 'stock.read', 'stock.receive', 'stock.transfer', 'stock.adjust', 'stock.count'],
      permissions_count: 9
    },
    {
      id: 'role_accountant',
      name: 'حسابدار',
      description: 'دسترسی به سوابق فروش، فاکتورهای خرید، گزارشات سود و زیان و ثبت تراکنش‌های مالی',
      permissions: ['dashboard.read', 'sales.read', 'sales.report', 'purchase.read', 'supplier.read', 'bill.read', 'customer.read', 'pos.payment'],
      permissions_count: 8
    }
  ]

  const displayRoles = items.length > 0 ? items : defaultRolesList

  const filteredRoles = displayRoles.filter((r: any) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.description && r.description.toLowerCase().includes(search.toLowerCase()))
  )

  const handleTestRole = (roleName: string, rolePerms?: string[]) => {
    switchActiveRole(roleName, rolePerms)
    toast.success(`نقش فعال به «${roleName}» تغییر یافت. اکنون بخش‌های غیرمجاز غیرفعال یا محدود شده‌اند.`, {
      duration: 4500,
      icon: '🛡️'
    })
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary-600 dark:text-primary-400" />
            نقش‌ها و سطح دسترسی‌ها
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            تعریف توضیحات دسترسی و انتخاب بخش‌های مجاز برای هر نقش (با غیرفعال‌سازی بخش‌های غیرعلامت‌زده)
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="gap-2 shadow-lg shadow-primary-500/20">
          <Plus className="w-4 h-4" />
          تعریف نقش جدید
        </Button>
      </motion.div>

      {/* Role Tester / Active Role Indicator Bar */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-4 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white shadow-xl border border-neutral-700/60"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 border border-primary-500/40 text-primary-400 flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-neutral-400">آزمایش نقش فعال سیستم:</span>
                <span className="px-2.5 py-0.5 rounded-full bg-primary-500 text-white text-xs font-bold">
                  {activeRoleName}
                </span>
                {activeRoleName !== 'مدیر سیستم' && (
                  <span className="inline-flex items-center gap-1 text-amber-400 text-xs font-medium">
                    <AlertCircle className="w-3.5 h-3.5" />
                    (محدودیت دسترسی فعال است)
                  </span>
                )}
              </div>
              <p className="text-xs text-neutral-300 mt-0.5">
                تغییر نقش باعث غیرفعال شدن بخش‌های غیرمجاز در منو و نمایش پیام «عدم دسترسی» هنگام ورود مستقیم می‌شود.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0">
            {['مدیر سیستم', 'صندوق‌دار', 'انباردار', 'حسابدار'].map((rName) => (
              <button
                key={rName}
                onClick={() => handleTestRole(rName)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  activeRoleName === rName
                    ? 'bg-white text-neutral-900 font-bold shadow-md'
                    : 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300 border border-neutral-700'
                }`}
              >
                {activeRoleName === rName && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                {rName}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="جستجوی نقش یا توضیحات دسترسی..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10"
          />
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
          <Info className="w-4 h-4 text-primary-500" />
          <span>تعداد نقش‌های تعریف‌شده: <strong className="text-neutral-900 dark:text-white font-bold">{displayRoles.length}</strong></span>
        </div>
      </div>

      {/* Roles Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-5 space-y-3">
              <Skeleton className="h-6 w-36" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredRoles.length === 0 ? (
        <div className="card p-12 text-center space-y-3">
          <Shield className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600" />
          <p className="text-neutral-600 dark:text-neutral-400 font-medium">نقشی با مشخصات جستجو شده یافت نشد</p>
          <Button variant="outline" size="sm" onClick={() => setSearch('')}>پاک کردن جستجو</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredRoles.map((role: any) => {
            const isCurrentActive = activeRoleName === role.name
            const permCount = role.permissions_count || (Array.isArray(role.permissions) ? role.permissions.length : 12)

            return (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className={`card p-6 flex flex-col justify-between transition-all duration-200 hover:shadow-xl relative overflow-hidden ${
                  isCurrentActive ? 'border-2 border-primary-500 ring-2 ring-primary-500/20' : ''
                }`}
              >
                {isCurrentActive && (
                  <div className="absolute top-0 left-0 bg-primary-500 text-white text-[10px] font-bold px-3 py-1 rounded-br-xl flex items-center gap-1 shadow-sm">
                    <Sparkles className="w-3 h-3" />
                    نقش فعال فعلی شما
                  </div>
                )}

                <div>
                  {/* Title & Actions */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-primary-100 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400 flex items-center justify-center font-bold text-lg shadow-sm">
                        <KeyRound className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-neutral-900 dark:text-white flex items-center gap-2">
                          {role.name}
                        </h3>
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                          <Layers className="w-3.5 h-3.5 text-primary-500" />
                          مجوز به {permCount} بخش از سیستم
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenModal(role)}
                        className="p-2 rounded-xl text-neutral-600 dark:text-neutral-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                        title="ویرایش دسترسی‌ها"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {role.name !== 'مدیر سیستم' && (
                        <button
                          onClick={() => deleteMutation.mutate(role.id)}
                          className="p-2 rounded-xl text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          title="حذف نقش"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Access Description (توضیح دسترسی) */}
                  <div className="p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-100 dark:border-neutral-800 my-3 text-xs text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    <span className="font-bold text-neutral-900 dark:text-white block mb-0.5">توضیحات دسترسی:</span>
                    {role.description || 'توضیحات دسترسی برای این نقش تنظیم نشده است.'}
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-2 mt-2">
                  <button
                    onClick={() => handleOpenModal(role)}
                    className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    ویرایش چک‌باکس دسترسی‌ها
                  </button>

                  <Button
                    size="sm"
                    variant={isCurrentActive ? 'secondary' : 'outline'}
                    onClick={() => handleTestRole(role.name, role.permissions)}
                    className="gap-1.5 text-xs"
                  >
                    {isCurrentActive ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        در حال استفاده
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-3.5 h-3.5" />
                        تست این نقش در پنل
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Modal: Define or Edit Role & Access Checkboxes */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-3xl shadow-2xl border border-neutral-200 dark:border-neutral-800 space-y-5 my-8 max-h-[90vh] flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0">
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                    <Shield className="w-6 h-6 text-primary-600" />
                    {editingRoleId ? 'ویرایش نقش و سطح دسترسی‌ها' : 'تعریف نقش جدید در سیستم'}
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    بخش‌های علامت‌زده‌شده برای کاربر فعال خواهد شد و مابقی بخش‌های برنامه غیرفعال و غیرقابل دسترس می‌گردند.
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 rounded-xl text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Content */}
              <form
                id="role-form"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.name.trim()) {
                    toast.error('لطفاً عنوان نقش را وارد کنید')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-5 overflow-y-auto flex-1 pr-1 pl-1"
              >
                {/* Inputs Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1.5">
                      عنوان نقش <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="مانند: مدیر فروش، انباردار، صندوق‌دار..."
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1.5">
                      توضیحات دسترسی
                    </label>
                    <Input
                      placeholder="توضیح کامل در مورد محدودیت‌ها و وظایف این نقش..."
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                </div>

                {/* Summary Info Box */}
                <div className="p-3 bg-primary-50 dark:bg-primary-950/30 rounded-xl border border-primary-200 dark:border-primary-900/50 flex items-center justify-between text-xs text-primary-900 dark:text-primary-200">
                  <span className="font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-primary-600" />
                    تعداد مجوزهای فعال‌کننده بخش‌ها: <strong className="text-sm underline">{form.permissions.length}</strong> مجوز
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, permissions: SYSTEM_PERMISSIONS.flatMap(g => g.permissions.map(p => p.key)) }))}
                      className="text-primary-700 dark:text-primary-300 hover:underline font-bold"
                    >
                      انتخاب همه بخش‌ها
                    </button>
                    <span>|</span>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, permissions: ['dashboard.read'] }))}
                      className="text-red-600 dark:text-red-400 hover:underline font-bold"
                    >
                      عدم انتخاب (محدودسازی کامل)
                    </button>
                  </div>
                </div>

                {/* Categorized Permission Checkboxes */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-primary-600" />
                    تنظیم چک‌باکس دسترسی به بخش‌های مختلف سیستم:
                  </h4>

                  <div className="space-y-4">
                    {SYSTEM_PERMISSIONS.map((group) => {
                      const groupKeys = group.permissions.map(p => p.key)
                      const isAllChecked = groupKeys.every(k => form.permissions.includes(k))
                      const isSomeChecked = groupKeys.some(k => form.permissions.includes(k))

                      return (
                        <div key={group.id} className="card p-4 bg-neutral-50/70 dark:bg-neutral-800/40 border border-neutral-200 dark:border-neutral-800 space-y-3">
                          {/* Group Title & Select All */}
                          <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700/60 pb-2">
                            <span className="font-bold text-sm text-neutral-900 dark:text-white flex items-center gap-2">
                              <Shield className="w-4 h-4 text-primary-500" />
                              {group.title}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleGroupPermissions(group.id)}
                              className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                            >
                              {isAllChecked ? (
                                <>
                                  <CheckSquare className="w-3.5 h-3.5" />
                                  لغو همه
                                </>
                              ) : (
                                <>
                                  <Square className="w-3.5 h-3.5" />
                                  انتخاب همه این بخش
                                </>
                              )}
                            </button>
                          </div>

                          {/* Permission items grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {group.permissions.map((perm) => {
                              const checked = form.permissions.includes(perm.key)

                              return (
                                <label
                                  key={perm.key}
                                  onClick={() => togglePermission(perm.key)}
                                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 ${
                                    checked
                                      ? 'bg-white dark:bg-neutral-800 border-primary-500 shadow-sm ring-1 ring-primary-500/20'
                                      : 'bg-neutral-100/60 dark:bg-neutral-900/40 border-neutral-200 dark:border-neutral-800 opacity-70 hover:opacity-100'
                                  }`}
                                >
                                  <div className="mt-0.5">
                                    {checked ? (
                                      <div className="w-5 h-5 rounded-md bg-primary-600 text-white flex items-center justify-center">
                                        <Check className="w-3.5 h-3.5" />
                                      </div>
                                    ) : (
                                      <div className="w-5 h-5 rounded-md border-2 border-neutral-300 dark:border-neutral-600" />
                                    )}
                                  </div>

                                  <div className="space-y-0.5">
                                    <span className="text-xs font-bold text-neutral-900 dark:text-white block">
                                      {perm.title}
                                    </span>
                                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-tight">
                                      {perm.description}
                                    </p>
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </form>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex-shrink-0">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  انصراف
                </Button>
                <Button type="submit" form="role-form" isLoading={createMutation.isPending} className="gap-2">
                  <Check className="w-4 h-4" />
                  ذخیره نقش و دسترسی‌ها
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
